import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getCardSetup, createCardSetupIntent, ApiError, type CardSetupInfo, type PaymentRail } from '../lib/api';

let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = (pk: string) => (stripePromise ??= loadStripe(pk));

type HyperInstance = {
  widgets: (o: { clientSecret: string }) => { create: (t: string, o?: unknown) => { mount: (selector: string) => void } };
  confirmPayment: (o: unknown) => Promise<{ error?: { message?: string } }>;
};
let hyperPromise: Promise<(pk: string) => HyperInstance> | null = null;
function loadHyper(): Promise<(pk: string) => HyperInstance> {
  const w = window as unknown as { Hyper?: (pk: string) => HyperInstance };
  if (w.Hyper) return Promise.resolve(w.Hyper);
  hyperPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    // Production HyperLoader. beta.hyperswitch.io is the sandbox build and
    // cannot drive a live pk_prd_ key: it crashed on the pay page with
    // "c.split is not a function" and never mounted the card form.
    s.src = 'https://checkout.hyperswitch.io/v1/HyperLoader.js';
    s.async = true;
    s.onload = () => (w.Hyper ? resolve(w.Hyper) : reject(new Error('Hyperswitch SDK unavailable')));
    s.onerror = () => reject(new Error('Failed to load Hyperswitch SDK'));
    document.head.appendChild(s);
  });
  return hyperPromise;
}

const shell: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  background: 'var(--p-background,#f6f7f9)', fontFamily: 'var(--font-product)', padding: '48px 16px',
};
const card: React.CSSProperties = {
  width: 460, maxWidth: '100%', background: '#fff', border: '1px solid var(--p-border)',
  borderRadius: 12, boxShadow: 'var(--p-shadow-lg)', padding: 28,
};

const RAIL_LABEL: Record<PaymentRail, string> = {
  stripe: 'Platform fees (Stripe)',
  hyperswitch: 'Pharmacy & shipping (Hyperswitch)',
};

export const AddCardPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState<CardSetupInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function reload() {
    if (!token) return;
    getCardSetup(token).then(setInfo).catch((e) => setErr(e instanceof ApiError ? e.message : 'Link not found'));
  }
  useEffect(reload, [token]);

  if (err && !info) return <div style={shell}><div style={card}><h2 style={{ margin: 0 }}>Link unavailable</h2><p style={{ color: 'var(--p-muted-fg)' }}>{err}</p></div></div>;
  if (!info) return <div style={shell}><div style={card}>Loading…</div></div>;

  return (
    <div style={shell}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <img src="/assets/5axis-icon.svg" alt="5 Axis" style={{ width: 26, height: 26 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>5 Axis Health</div>
            <div style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>Payment method for {info.clientName}</div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--p-muted-fg)', marginTop: 0 }}>
          Add a card to keep on file. It’s stored securely and used to pay future invoices automatically.
        </p>

        {info.rails.map((r) => (
          <RailSection key={r.rail} token={token!} rail={r.rail} onFile={r.onFile} current={r.current} onSaved={reload} />
        ))}
      </div>
    </div>
  );
};

function RailSection({
  token, rail, onFile, current, onSaved,
}: {
  token: string;
  rail: PaymentRail;
  onFile: boolean;
  current: { brand?: string; last4?: string } | null;
  onSaved: () => void;
}) {
  const [intent, setIntent] = useState<{ clientSecret: string; publishableKey: string | null } | null>(null);
  const [starting, setStarting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setStarting(true);
    setError(null);
    try {
      const r = await createCardSetupIntent(token, rail);
      if (!r.publishableKey) throw new Error('Card processor not configured');
      setIntent({ clientSecret: r.clientSecret, publishableKey: r.publishableKey });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start card setup');
    } finally {
      setStarting(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid var(--p-border)', paddingTop: 16, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>{RAIL_LABEL[rail]}</strong>
        {onFile && current && (
          <span style={{ fontSize: 12, color: 'var(--status-success-ink,#15803d)' }}>
            On file: {(current.brand ?? 'card').toUpperCase()} •••• {current.last4}
          </span>
        )}
      </div>

      {done ? (
        <div style={{ padding: 12, borderRadius: 8, background: 'rgba(21,128,61,.08)', color: 'var(--status-success-ink,#15803d)', fontWeight: 600 }}>
          ✓ Card saved — thank you.
        </div>
      ) : !intent ? (
        <>
          {error && <div style={{ marginBottom: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
          <button className="btn btn--primary" disabled={starting} onClick={start} style={{ width: '100%' }}>
            {starting ? 'Preparing…' : onFile ? 'Replace card' : 'Add card'}
          </button>
        </>
      ) : rail === 'stripe' ? (
        <Elements stripe={getStripe(intent.publishableKey!)} options={{ clientSecret: intent.clientSecret, appearance: { theme: 'stripe' } }}>
          <StripeSetup onDone={() => { setDone(true); onSaved(); }} />
        </Elements>
      ) : (
        <HyperSetup clientSecret={intent.clientSecret} publishableKey={intent.publishableKey!} onDone={() => { setDone(true); onSaved(); }} />
      )}
    </div>
  );
}

function StripeSetup({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const { error: err } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    setBusy(false);
    if (err) setError(err.message ?? 'Card could not be saved');
    else onDone();
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      {error && <div style={{ marginTop: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
      <button type="submit" className="btn btn--primary" disabled={!stripe || busy} style={{ marginTop: 14, width: '100%' }}>
        {busy ? 'Saving…' : 'Save card'}
      </button>
    </form>
  );
}

function HyperSetup({ clientSecret, publishableKey, onDone }: { clientSecret: string; publishableKey: string; onDone: () => void }) {
  const mountRef = useRef<HTMLDivElement>(null);
  // HyperLoader's mount() resolves a CSS selector itself and calls .split on
  // what it is given — passing the DOM node threw "c.split is not a function"
  // and the card form never appeared. Stripe Elements takes a node; this does not.
  const mountId = useRef(`hyper-mount-${Math.random().toString(36).slice(2)}`).current;
  const ctx = useRef<{ hyper?: HyperInstance; widgets?: ReturnType<HyperInstance['widgets']> }>({});
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadHyper()
      .then((Hyper) => {
        if (cancelled || !mountRef.current) return;
        const hyper = Hyper(publishableKey);
        const widgets = hyper.widgets({ clientSecret });
        widgets.create('payment').mount(`#${mountId}`);
        ctx.current = { hyper, widgets };
        setReady(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load card form'));
    return () => { cancelled = true; };
  }, [clientSecret, publishableKey]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { hyper, widgets } = ctx.current;
    if (!hyper || !widgets) return;
    setBusy(true);
    setError(null);
    try {
      const res = await hyper.confirmPayment({ widgets, confirmParams: { return_url: window.location.href }, redirect: 'if_required' });
      if (res?.error) setError(res.error.message ?? 'Card could not be saved');
      else onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Card could not be saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div ref={mountRef} id={mountId} />
      {!ready && !error && <div style={{ color: 'var(--p-muted-fg)' }}>Preparing…</div>}
      {error && <div style={{ marginTop: 10, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
      <button type="submit" className="btn btn--primary" disabled={!ready || busy} style={{ marginTop: 14, width: '100%' }}>
        {busy ? 'Saving…' : 'Save card'}
      </button>
    </form>
  );
}
